import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getProductionByDepartment,
  getProductionByDepartmentOverview,
} from '@services/api/dashboard/finance.api'
import type {
  PeriodComparisonMetric,
  ProductionByDepartmentResult,
  ProductionByDepartmentRow,
} from '@services/api/dashboard/finance.api'
import {
  computeChartAxis,
  filterYtdPeriodItems,
  periodBucketDollars,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'

export type UseProductionByDepartmentOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type ProductionByDepartmentTrendChartRow = {
  label: string
  dentist: number
  hygienist: number
}

export type ProductionByDepartmentDonutSlice = {
  name: string
  value: number
  color: string
}

export type UseProductionByDepartmentResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  /** Any of dentist / hygienist / production-per-visit is nonzero. */
  hasData: boolean
  /** Dentist or hygienist production is nonzero (department split / donut). */
  hasDentistHygienistData: boolean
  /** Production per hygiene visit is nonzero. */
  hasProductionPerVisitData: boolean
  data: ProductionByDepartmentResult | null
  dentistMetric: PeriodComparisonMetric | undefined
  hygienistMetric: PeriodComparisonMetric | undefined
  productionPerVisitMetric: PeriodComparisonMetric | undefined
  trendChart: {
    data: ProductionByDepartmentTrendChartRow[]
    yAxisDomain: [number, number]
    yAxisTicks: number[]
  }
  donutSlices: ProductionByDepartmentDonutSlice[]
  rows: ProductionByDepartmentRow[]
}

function useProductionByDepartmentFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseProductionByDepartmentOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getProductionByDepartment>>
  >
}): UseProductionByDepartmentResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const dentistMetric = data?.dentistProduction
  const hygienistMetric = data?.hygienistProduction
  const productionPerVisitMetric = data?.productionPerVisit

  const trendChart = useMemo(() => {
    const dentistRaw = data?.dentistPeriodSummary ?? []
    const hygienistRaw = data?.hygienistPeriodSummary ?? []
    const paired = filterYtdPeriodItems(
      range,
      date,
      dentistRaw.map((dentist, index) => ({
        dentist,
        hygienist: hygienistRaw[index],
      })),
      (item) => item.dentist.label,
    )
    const chartData: ProductionByDepartmentTrendChartRow[] = paired.map(
      ({ dentist, hygienist }) => ({
        label: dentist.label,
        dentist: periodBucketDollars(dentist) / 1000,
        hygienist: periodBucketDollars(hygienist ?? {}) / 1000,
      }),
    )
    const values = chartData.flatMap((d) => [d.dentist, d.hygienist])
    const axis = computeChartAxis(values)
    return {
      data: chartData,
      yAxisDomain: axis.yDomain,
      yAxisTicks: axis.yTicks,
    }
  }, [data, range, date])

  const donutSlices = useMemo((): ProductionByDepartmentDonutSlice[] => {
    const dentist = dentistMetric?.current ?? 0
    const hygienist = hygienistMetric?.current ?? 0
    return [
      { name: 'Dentist', value: dentist, color: 'var(--indigo-400)' },
      { name: 'Hygienist', value: hygienist, color: 'var(--indigo-300)' },
    ]
  }, [dentistMetric, hygienistMetric])

  const rows = useMemo(
    () =>
      filterYtdPeriodItems(range, date, data?.rows ?? [], (row) => row.period),
    [data, range, date],
  )

  const hasDentistHygienistData = hasNonZeroDashboardValues(
    data?.dentistProduction.current,
    data?.hygienistProduction.current,
  )
  const hasProductionPerVisitData = hasNonZeroDashboardValues(
    data?.productionPerVisit.current,
  )

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: hasDentistHygienistData || hasProductionPerVisitData,
    hasDentistHygienistData,
    hasProductionPerVisitData,
    data,
    dentistMetric,
    hygienistMetric,
    productionPerVisitMetric,
    trendChart,
    donutSlices,
    rows,
  }
}

export function useProductionByDepartment(
  options: UseProductionByDepartmentOptions,
): UseProductionByDepartmentResult {
  return useProductionByDepartmentFromFetcher({
    ...options,
    fetcher: getProductionByDepartment,
  })
}

export function useProductionByDepartmentOverview(
  options: UseProductionByDepartmentOptions,
): UseProductionByDepartmentResult {
  return useProductionByDepartmentFromFetcher({
    ...options,
    fetcher: getProductionByDepartmentOverview,
  })
}
